import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Tipos de usuário e suas permissões
export const USER_ROLES = {
  ADMIN: 'Admin',
  WILLIANS: 'Willians',
  GESTOR: 'Gestor',
  SUPERVISOR: 'Supervisor',
  TECNICO: 'Técnico',
  CLIENTE: 'Cliente',
  SUPORTE: 'Suporte',
  MANUTENCAO: 'Manutenção',
  VISUALIZACAO: 'Visualização',
};

export const PERMISSIONS = {
  [USER_ROLES.ADMIN]: {
    pages: ['*'], // Acesso a todas as páginas
    actions: ['create', 'read', 'update', 'delete'],
  },
  [USER_ROLES.WILLIANS]: {
    pages: ['*'], // Acesso a todas as páginas
    actions: ['create', 'read', 'update', 'delete'],
  },
  [USER_ROLES.SUPORTE]: {
    pages: ['/relatorio-obras', '/relatorios-consulta', '/chamados'],
    actions: ['create', 'read', 'update'],
  },
  [USER_ROLES.MANUTENCAO]: {
    pages: ['/testes-loja-menu', '/testes-loja', '/testes-ar-condicionado'],
    actions: ['create', 'read', 'update'],
  },
  [USER_ROLES.VISUALIZACAO]: {
    pages: ['/testes-loja-menu', '/testes-loja', '/testes-ar-condicionado'],
    actions: ['read'],
  },
};

const AuthContext = createContext();

const decodeJwtPayload = (jwtToken) => {
  if (!jwtToken || typeof jwtToken !== 'string') return null;

  const parts = jwtToken.split('.');
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const normalized = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(window.atob(normalized));
  } catch {
    return null;
  }
};

const isTokenExpired = (jwtToken, toleranceSeconds = 20) => {
  const payload = decodeJwtPayload(jwtToken);
  const exp = Number(payload?.exp || 0);
  if (!exp) return false;

  const now = Math.floor(Date.now() / 1000);
  return exp <= now + toleranceSeconds;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, []);

  const hydrateSession = useCallback(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (!savedToken || !savedUser) {
      clearSession();
      return;
    }

    if (isTokenExpired(savedToken)) {
      clearSession();
      return;
    }

    try {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    } catch {
      clearSession();
    }
  }, [clearSession]);

  // Verificar se há token salvo ao carregar a página
  useEffect(() => {
    hydrateSession();
    setLoading(false);
  }, [hydrateSession]);

  const login = (token, userData) => {
    setToken(token);
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const validateSession = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!storedToken || !storedUser) {
      if (isAuthenticated) clearSession();
      return;
    }

    if (isTokenExpired(storedToken)) {
      clearSession();
      return;
    }

    // Re-hidrata estado em memória se necessário (aba ficou muito tempo suspensa)
    if (token !== storedToken) {
      hydrateSession();
    }

    try {
      const res = await fetch('/api/me/', {
        headers: { Authorization: `Bearer ${storedToken}` },
      });

      if (res.status === 401 || res.status === 403) {
        clearSession();
      }
    } catch {
      // Em falha de rede não derruba a sessão.
    }
  }, [clearSession, hydrateSession, isAuthenticated, token]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const handleFocus = () => {
      validateSession();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        validateSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, validateSession]);

  const hasPermission = (page, action = 'read') => {
    // Sistema simplificado baseado apenas em nivel_acesso
    const role = (user?.nivel_acesso || '').toString().toLowerCase();
    let allowedCards = [];
    try {
      const parsedCards = user?.cards_permitidos
        ? typeof user.cards_permitidos === 'string'
          ? JSON.parse(user.cards_permitidos)
          : user.cards_permitidos
        : [];
      allowedCards = Array.isArray(parsedCards) ? parsedCards : [];
    } catch {
      allowedCards = [];
    }

    // Normalizar página (remoção de parâmetros/query, garantir prefixo '/')
    const normalize = (p) => {
      if (!p) return '';
      try {
        // remove query/hash
        return p.split('?')[0].split('#')[0];
      } catch {
        return p;
      }
    };
    const currentPage = normalize(page);

    if (currentPage === '/agente') {
      if (role === 'gestor' || role === USER_ROLES.GESTOR.toLowerCase()) {
        return true;
      }
      if (allowedCards.includes('Agente')) {
        return true;
      }
    }

    // Admin e Willians: acesso total
    if (
      role === 'admin' ||
      role === 'willians' ||
      role === USER_ROLES.ADMIN.toLowerCase() ||
      role === USER_ROLES.WILLIANS.toLowerCase()
    ) {
      return true;
    }

    // Páginas de livre leitura para todos os perfis autenticados (read-only)
    const publicReadPages = [
      '/relatorio-obras', // listagem
      '/relatorios-consulta', // consulta moderna com métricas e dashboard
      '/estrutura', // Módulo de Estrutura
      '/chamados', // Módulo de Chamados
      '/recebimento-os', // Recebimento OS/NFE
    ];

    // Páginas com acesso total (CRUD) para usuários autenticados
    const fullAccessPages = [
      '/recebimento-os', // Todos podem criar/editar/visualizar recebimentos
    ];

    // Verificar acesso total a páginas específicas
    if (fullAccessPages.some((fp) => currentPage === fp || currentPage.startsWith(fp + '/'))) {
      // Permitir todas as ações exceto delete para não-admin
      if (action !== 'delete') {
        return true;
      }
    }

    if (
      action === 'read' &&
      publicReadPages.some((pr) => currentPage === pr || currentPage.startsWith(pr + '/'))
    ) {
      return true;
    }

    // Suporte: acesso total a relatório de obras e chamados (CRUD, sem excluir)
    if (role === 'suporte' || role === USER_ROLES.SUPORTE.toLowerCase()) {
      const suportePages = ['/relatorio-obras', '/relatorios-consulta', '/chamados'];
      if (suportePages.some((sp) => currentPage === sp || currentPage.startsWith(sp + '/'))) {
        return action !== 'delete'; // Suporte não pode excluir
      }
      return false;
    }

    // Manutenção: acesso a /testes-loja, /relatorio-obras, /chamados (apenas leitura + update limitado)
    if (role === 'manutencao' || role === USER_ROLES.MANUTENCAO.toLowerCase()) {
      // Testes de loja: acesso total
      if (
        currentPage === '/testes-loja' ||
        currentPage === '/testes-loja-menu' ||
        currentPage.startsWith('/testes-ar-condicionado') ||
        currentPage.startsWith('/testes-elevador')
      ) {
        return true;
      }
      // Relatório de obras: pode criar e editar, mas NÃO pode excluir
      if (currentPage.startsWith('/relatorio-obras')) {
        if (action === 'read' || action === 'create' || action === 'update') {
          return true;
        }
        return false;
      }
      // Chamados: manutenção pode ver e dar "Ok" (update limitado), mas NÃO pode criar/excluir
      if (currentPage === '/chamados' || currentPage.startsWith('/chamados/')) {
        if (action === 'read' || action === 'update') {
          return true;
        }
        return false; // não pode create nem delete
      }
      return false;
    }

    // Visualização: apenas leitura nas páginas públicas + testes
    if (role === 'visualizacao' || role === USER_ROLES.VISUALIZACAO.toLowerCase()) {
      if (action !== 'read') return false;
      if (
        currentPage === '/testes-loja' ||
        currentPage === '/testes-loja-menu' ||
        currentPage.startsWith('/testes-ar-condicionado') ||
        currentPage.startsWith('/testes-elevador')
      ) {
        return true;
      }
      // Já coberto: páginas públicas com read
      return publicReadPages.some((pr) => currentPage === pr || currentPage.startsWith(pr + '/'));
    }

    // Gestor: acesso a tudo EXCETO Admin e Log de Acesso
    if (role === 'gestor' || role === USER_ROLES.GESTOR.toLowerCase()) {
      // Bloquear apenas páginas admin específicas
      if (
        currentPage === '/admin' ||
        currentPage === '/access-log' ||
        currentPage === '/cadastro-usuarios'
      ) {
        return false;
      }
      // Todas as outras páginas: acesso total
      return true;
    }

    // Supervisor e Técnico: Consulta Relatório de Obras, Receber OS/NFE, Consultar/Abrir Chamados
    if (
      role === 'supervisor' ||
      role === USER_ROLES.SUPERVISOR.toLowerCase() ||
      role === 'tecnico' ||
      role === 'técnico' ||
      role === USER_ROLES.TECNICO.toLowerCase()
    ) {
      const supervisorPages = ['/relatorios-consulta', '/recebimento-os', '/chamados', '/testes-loja-menu', '/testes-loja', '/testes-ar-condicionado'];
      if (supervisorPages.some((sp) => currentPage === sp || currentPage.startsWith(sp + '/'))) {
        return action !== 'delete'; // Não pode excluir
      }
      return false;
    }

    // Cliente: apenas Abrir Chamado e Consultar Chamados
    if (role === 'cliente' || role === USER_ROLES.CLIENTE.toLowerCase()) {
      // Chamados: pode criar (abrir) e listar (consultar)
      if (currentPage === '/chamados' || currentPage.startsWith('/chamados/')) {
        return action === 'read' || action === 'create';
      }
      return false;
    }

    // Caso não reconhecido: negar acesso
    return false;
  };

  const getUserRoleLabel = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return 'Administrador';
      case USER_ROLES.WILLIANS:
        return 'Willians';
      case USER_ROLES.SUPORTE:
        return 'Suporte';
      case USER_ROLES.MANUTENCAO:
        return 'Manutenção';
      case USER_ROLES.VISUALIZACAO:
        return 'Visualização';
      default:
        return 'Indefinido';
    }
  };

  const isAdmin = () => {
    const role = (user?.nivel_acesso || '').toString().toLowerCase();
    return (
      role === 'admin' ||
      role === 'willians' ||
      role === USER_ROLES.ADMIN.toLowerCase() ||
      role === USER_ROLES.WILLIANS.toLowerCase()
    );
  };

  const isSuporte = () => {
    const role = (user?.nivel_acesso || '').toString().toLowerCase();
    return role === 'suporte' || role === USER_ROLES.SUPORTE.toLowerCase();
  };

  const isManutencao = () => {
    const role = (user?.nivel_acesso || '').toString().toLowerCase();
    return (
      role === 'manutencao' || role === 'manutenção' || role === USER_ROLES.MANUTENCAO.toLowerCase()
    );
  };

  const isGestor = () => {
    const role = (user?.nivel_acesso || '').toString().toLowerCase();
    return role === 'gestor' || role === USER_ROLES.GESTOR.toLowerCase();
  };

  const isSupervisor = () => {
    const role = (user?.nivel_acesso || '').toString().toLowerCase();
    return role === 'supervisor' || role === USER_ROLES.SUPERVISOR.toLowerCase();
  };

  const isTecnico = () => {
    const role = (user?.nivel_acesso || '').toString().toLowerCase();
    return role === 'tecnico' || role === 'técnico' || role === USER_ROLES.TECNICO.toLowerCase();
  };

  const isCliente = () => {
    const role = (user?.nivel_acesso || '').toString().toLowerCase();
    return role === 'cliente' || role === USER_ROLES.CLIENTE.toLowerCase();
  };

  const canAccessTests = () => {
    return hasPermission('/testes-loja-menu', 'read');
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    logout,
    hasPermission,
    getUserRoleLabel,
    isAdmin,
    isSuporte,
    isManutencao,
    isGestor,
    isSupervisor,
    isTecnico,
    isCliente,
    canAccessTests,
    validateSession,
    USER_ROLES,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
